import { useEffect, useRef, useCallback } from 'react';
import { 
  Chart, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  LineController, 
  Title, 
  Tooltip, 
  Filler, 
  Legend 
} from 'chart.js';
import type { ChartDataPoint } from '../../lib/chartUtils';
import { Card, CardContent } from '../../components/ui/card';
import { cn } from '../../lib/utils';

type AreaChartProps = {
  title: string;
  data: (ChartDataPoint & { timeRange?: string })[];
  avgValue?: number;
  color?: string;
  height?: number;
  className?: string;
};

// Register only needed components (reduces bundle size)
Chart.register(CategoryScale, LinearScale, PointElement, LineController, LineElement, Title, Tooltip, Filler, Legend);

// Data validation helper (be tolerant of time types)
const isValidDataPoint = (point: any): point is Partial<ChartDataPoint> => {
  return (
    point &&
    typeof point === 'object' &&
    point.time !== undefined &&
    (typeof point.value === 'number' || typeof point.value === 'string')
  );
};

export function AreaChart({ 
  title, 
  data, 
  avgValue, 
  color = '#1a56db', 
  height = 250,
  className 
}: AreaChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Safe chart destruction
  const destroyChart = useCallback(() => {
    if (chartInstance.current) {
      try {
        chartInstance.current.destroy();
        chartInstance.current = null;
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    }
  }, []);

  // Data sanitization
  const sanitizedData = useCallback(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    
    return data
      .filter(isValidDataPoint)
      .map(point => ({
        ...point,
        time: typeof point.time === 'string' ? point.time : (() => {
          try {
            const d = new Date(point.time as any);
            return isNaN(d.getTime()) ? String(point.time) : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } catch {
            return String(point.time);
          }
        })(),
        value: typeof point.value === 'string' ? parseFloat(point.value) || 0 : point.value
      }));
  }, [data]);

  const createChart = useCallback(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Ensure any existing chart on this canvas is destroyed
    const existing = Chart.getChart(chartRef.current);
    if (existing) {
      try { existing.destroy(); } catch {}
    }

    const processedData = sanitizedData();
    if (processedData.length === 0) {
      destroyChart();
      try {
        chartInstance.current = new Chart(ctx, {
          type: 'line',
          data: { labels: [], datasets: [{ label: 'Series', data: [] }] },
          options: { responsive: true, maintainAspectRatio: false }
        });
      } catch (error) {
        console.warn('No valid data points for chart and failed to render empty chart:', error);
      }
      return;
    }

    try {
      destroyChart(); // Ensure previous chart is destroyed

      // Create gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, `${color}33`);
      gradient.addColorStop(1, `${color}00`);

      // Chart configuration
      const safeTitle = typeof title === 'string' ? title : '';
      const isNetwork = safeTitle.toLowerCase().includes('network') || safeTitle.toLowerCase().includes('traffic');
      const yUnit = isNetwork ? 'Mbps' : '%';

      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
         labels: processedData.map(point => {
                    if (point.fullDate) {
                      const d = new Date(point.fullDate);
                      const timeRange = point.timeRange || '';

                      // Only show date for weekly and monthly views
                      if (timeRange === '1w' || timeRange === '1m') {
                        return d.toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        });
                      } 
                      // For 24h and below show **only time - no date**
                      else {
                        return d.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                    }
                    return point.time;
                  }),

          datasets: [{
            label: safeTitle || 'Series',
            data: processedData.map(point => point.value),
            borderColor: color,
            backgroundColor: gradient,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: true,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: '#1f2937',
              titleColor: '#fff',
              bodyColor: '#fff',
              callbacks: {
               title: function(context) {
                    const point = processedData[context[0].dataIndex];
                    if (!point) return '';

                    if (point.fullDate) {
                      const d = new Date(point.fullDate);
                      const timeRange = point.timeRange || '';

                      // Show date + time only for weekly/monthly views
                      if (timeRange === '1w' || timeRange === '1m') {
                        return d.toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      } 
                      // For 24h and below show only time (no date)
                      else {
                        return d.toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        });
                      }
                    }
                    return point.time;
                  },

                label: function(context) {
                  const value = Number(context.parsed.y);
                  return `${title}: ${isFinite(value) ? value.toFixed(2) : '0'}${yUnit}`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { 
                color: '#9ca3af', 
                maxTicksLimit: 8,
                 callback: function(val, index) {
                    const point = processedData[val as number];
                    if (!point?.fullDate) return point?.time || '';

                    const d = new Date(point.fullDate);
                    const timeRange = point.timeRange || '24h';

                    // Only date for weekly or monthly view
                    if (timeRange === '1w' || timeRange === '1m') {
                      return d.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric'
                      });
                    }
                    // For 24h and shorter ranges show only time without date
                    else {
                      if (index === 0 || index % 3 === 0) {
                        return d.toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                      return '';
                    }
                  }
                }
              },
            y: {
              beginAtZero: true,
              grid: { color: '#e5e7eb' },
              ticks: {
                color: '#9ca3af',
                callback: function(value) {
                  const num = typeof value === 'string' ? Number(value) : value as number;
                  return isFinite(num) ? `${num} ${yUnit}` : `0 ${yUnit}`;
                }
              },
              max: isNetwork ? undefined : 100
            }
          }
        }
      });

    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }, [data, color, height, title, destroyChart, sanitizedData]);

  useEffect(() => {
    createChart();
    return destroyChart;
  }, [createChart, destroyChart]);

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <CardContent className="p-5">
          <h3 className="text-base font-medium text-neutral-dark mb-4">{title}</h3>
          <div 
            style={{ height: `${height}px` }} 
            className="flex items-center justify-center bg-gray-50 rounded-lg"
          >
            <div className="text-center text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p>No data available</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-neutral-dark">{title}</h3>
          {avgValue !== undefined && (
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">Average:</span>
              <span className="text-sm font-medium text-neutral-dark">
                {(() => {
                  const isNetwork = title.toLowerCase().includes('network') || title.toLowerCase().includes('traffic');
                  const value = isFinite(avgValue) ? avgValue.toFixed(2) : '0';
                  return isNetwork ? `${value} Mbps` : `${value}%`;
                })()}
              </span>
            </div>
          )}
        </div>
        <div style={{ height: `${height}px` }} className="relative">
          <canvas ref={chartRef} />
        </div>
      </CardContent>
    </Card>
  );
}
